import React from "react";
import { AnalyticsGranularity } from "../../api/analyticsAdmin";

// Shared chart styling for the activity overview charts (line, stacked bars,
// composition pie) used by OverviewTab and BreakdownRow.

const GRANULARITIES: AnalyticsGranularity[] = ["Day", "Week", "Month"];

// Day/Week/Month toggle shown in the top-right of every time-bucketed chart.
// All instances are driven by one shared granularity state in OverviewTab, so
// changing it on any chart mirrors to the rest.
export const GranularityToggle: React.FC<{
  value: AnalyticsGranularity;
  onChange: (granularity: AnalyticsGranularity) => void;
}> = ({ value, onChange }) => (
  <div className="flex gap-1">
    {GRANULARITIES.map((g) => (
      <button
        key={g}
        onClick={() => onChange(g)}
        className={`px-3 py-1 text-sm rounded border ${
          value === g
            ? "border-primary bg-primary/10 text-primary"
            : "border-gray-300 text-gray-600 hover:bg-gray-50"
        }`}
      >
        {g}
      </button>
    ))}
  </div>
);

export const SERIES_COLORS = [
  "rgb(99 102 241 / 0.85)",
  "rgb(34 197 94 / 0.85)",
  "rgb(236 72 153 / 0.85)",
  "rgb(245 158 11 / 0.85)",
  "rgb(6 182 212 / 0.85)",
  "rgb(139 92 246 / 0.85)",
];
export const UNKNOWN_COLOR = "rgb(209 213 219 / 0.85)";

// Residual / unattributed segments always render grey rather than taking a
// palette colour, so they read as "not a real cohort" across every chart.
const GRAY_KEYS = new Set(["unknown", "anonymous"]);

export const colorFor = (key: string, index: number): string =>
  GRAY_KEYS.has(key) ? UNKNOWN_COLOR : SERIES_COLORS[index % SERIES_COLORS.length];

// Build a stable value -> color map from an ordered list of series keys. Both
// the stacked bar chart and its companion pie look colours up by value through
// this map, so a given segment (e.g. "ios") always renders in the same colour
// regardless of the order each chart happens to receive its data in.
export const buildColorMap = (keys: string[]): Map<string, string> =>
  new Map(keys.map((key, index) => [key, colorFor(key, index)]));

// Vertical space each category occupies in a horizontal breakdown bar chart.
// It has to fit the grouped bars plus the segment label that sits above them.
export const SEGMENT_ROW_HEIGHT = 76;

// Custom Y-axis tick for the horizontal breakdown bar charts. Long segment
// slugs (e.g. "mobil_underholdning") never fit in a left gutter without either
// clipping or eating half the chart, so instead of a right-aligned label beside
// the axis we render the name left-aligned *above* each bar group, where it can
// span the chart's full width regardless of length.
export const CategoryBarTick: React.FC<{
  x?: number;
  y?: number;
  payload?: { value: string };
  labelFn?: (name: string) => string;
}> = ({ x = 0, y = 0, payload, labelFn = (name) => name }) => (
  <text
    x={x}
    y={y}
    dx={12}
    dy={-32}
    textAnchor="start"
    fontSize={14}
    className="fill-gray-600"
  >
    {labelFn(payload?.value ?? "")}
  </text>
);

const RADIAN = Math.PI / 180;

// Always-visible share labels rendered inside each pie slice. Slices under 5%
// are left unlabelled to avoid overlapping text; the legend names every slice.
export const renderPieLabel = (props: {
  cx: number;
  cy: number;
  midAngle: number;
  innerRadius: number;
  outerRadius: number;
  percent: number;
}): React.ReactNode => {
  const { cx, cy, midAngle, innerRadius, outerRadius, percent } = props;
  if (!percent || percent < 0.05) return null;
  const radius = innerRadius + (outerRadius - innerRadius) / 2;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);
  return (
    <text
      x={x}
      y={y}
      textAnchor="middle"
      dominantBaseline="central"
      className="fill-white text-[11px] font-medium"
    >
      {`${Math.round(percent * 100)}%`}
    </text>
  );
};
